import React from 'react';
import { Box, TextField, useMediaQuery } from '@mui/material';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import CustomTitleWithBlueBackground from './CustomTitleWithBlueBackground';
import { useThemeContext } from '../../contexts/ThemeContext';
import { Turnstile } from '@marsidev/react-turnstile';

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const ContactForm = ({
  imageSrc,
  formData,
  setFormData,
  onSubmit,
  showNameEmail,
  onTurnstileSuccess,
  turnstileRef,
}) => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '16px',
      }}
    >
      {isMobile ? (
        <></>
      ) : (
        <Box
          component="img"
          src={imageSrc}
          alt="Contact Form Image"
          sx={{
            width: '100%',
            maxWidth: '450px',
            borderRadius: '16px',
            marginBottom: '24px',
          }}
        />
      )}

      <CustomTitleWithBlueBackground text="Send us a message" />

      {showNameEmail && (
        <>
          <TextField
            fullWidth
            label="Name"
            name="name"
            placeholder="Your Name"
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            sx={{
              input: {
                color: theme.support.contactForm.placeholder.color,
                borderRadius:
                  'var(--Spacing-1, 4px) var(--Spacing-1, 4px) var(--Spacing-0, 0px) var(--Spacing-0, 0px);',
              },
              label: {
                color: theme.support.contactForm.placeholder.color,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Default border color
                },
                '&:hover fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Hover border color
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Focused border color
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.support.contactForm.label.color,
                backgroundColor: theme.support.contactForm.label.backgroundColor,
                padding: '4px',
                fontWeight: 'medium',
              },
              marginTop: '16px',
              marginBottom: '16px',
            }}
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            placeholder="Your Email"
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange}
            sx={{
              input: {
                color: theme.support.contactForm.placeholder.color,
                borderRadius:
                  'var(--Spacing-1, 4px) var(--Spacing-1, 4px) var(--Spacing-0, 0px) var(--Spacing-0, 0px);',
              },
              label: {
                color: theme.support.contactForm.placeholder.color,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Default border color
                },
                '&:hover fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Hover border color
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.support.contactForm.border.color, // Focused border color
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.support.contactForm.label.color,
                backgroundColor: theme.support.contactForm.label.backgroundColor,
                padding: '4px',
                fontWeight: 'medium',
              },
              marginBottom: '16px',
            }}
          />
        </>
      )}

      <TextField
        fullWidth
        label="Subject"
        name="subject"
        placeholder="What is this message about?"
        variant="outlined"
        value={formData.subject}
        onChange={handleInputChange}
        sx={{
          input: {
            color: theme.support.contactForm.placeholder.color,
            borderRadius: 'var(--Spacing-1, 4px) var(--Spacing-1, 4px) var(--Spacing-0, 0px) var(--Spacing-0, 0px);',
          },
          label: {
            color: theme.support.contactForm.placeholder.color,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.support.contactForm.border.color, // Default border color
            },
            '&:hover fieldset': {
              borderColor: theme.support.contactForm.border.color, // Hover border color
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.support.contactForm.border.color, // Focused border color
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.support.contactForm.label.color,
            backgroundColor: theme.support.contactForm.label.backgroundColor,
            padding: '4px',
            fontWeight: 'medium',
          },
          marginTop: showNameEmail ? '' : '16px',
          marginBottom: '16px',
        }}
      />

      <TextField
        fullWidth
        label="Message"
        name="message"
        placeholder="Type your message"
        variant="outlined"
        multiline
        rows={3}
        value={formData.message}
        onChange={handleInputChange}
        sx={{
          input: {
            color: theme.support.contactForm.placeholder.color,
            borderRadius: 'var(--Spacing-1, 4px) var(--Spacing-1, 4px) var(--Spacing-0, 0px) var(--Spacing-0, 0px);',
          },
          label: {
            color: theme.support.contactForm.placeholder.color,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.support.contactForm.border.color, // Default border color
            },
            '&:hover fieldset': {
              borderColor: theme.support.contactForm.border.color, // Hover border color
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.support.contactForm.border.color, // Focused border color
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.support.contactForm.label.color,
            backgroundColor: theme.support.contactForm.label.backgroundColor,
            padding: '4px',
            fontWeight: 'medium',
          },
          marginBottom: '16px',
        }}
      />

      <Box sx={{ mb: 2 }}>
        <Turnstile
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          onSuccess={onTurnstileSuccess}
          options={{
            theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
          }}
        />
      </Box>

      {/* Submit Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-end',
          width: '100%',
          marginTop: isMobile ? '24px' : '0',
        }}
      >
        <PrimaryBlueButton
          buttonText={'Submit'}
          onClick={handleSubmit}
          sxProps={{
            width: isMobile ? '230px' : '120px',
          }}
        />
      </Box>
    </Box>
  );
};

export default ContactForm;
